
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton, Pagination
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import apiUrl from '../../Config';
import { Pencil, Plus } from 'lucide-react';

const DisplaySubCategory = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { service } = state || {};
    const [categories, setCategories] = useState([]);
    const [subServices, setSubServices] = useState([]);
    const [selectedCat, setSelectedCat] = useState(service?.id || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${apiUrl}/categories/getallcategories`);
                setCategories(res.data.category || []);
            } catch (error) {
                const message = error?.response?.data?.message || error.message || 'Failed to fetch sub categories'
                setError(message)
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Fetch subcategories based on selected category or all
    const fetchSubService = async () => {
        setLoading(true);
        setError("");
        try {
            let response;
            if (selectedCat) {
                response = await axios.get(`${apiUrl}/subcategories/getsubcategoriesbycategory?page=${page}&limit=${limit}&categoryId=${selectedCat}`);
            } else {
                response = await axios.get(`${apiUrl}/subcategories/getallsubcategories?page=${page}&limit=${limit}`, {
                    page,
                    limit
                });
            }

            if (response?.data?.status === true) {
                setSubServices(response?.data?.subCategories || []);
                // console.log(response?.data,"lehfihew")
                setTotalPages(response.data.totalPages || 1);
                setTotalItems(response.data.totalItems || (response.data.subCategories?.length || 0));
            } else {
                setSubServices([]);
                setError("Fetching subcategories failed.");
                setTotalPages(1);
                setTotalItems(0);
            }
        } catch (error) {
            setSubServices([]);
            setError("Something went wrong. Please try again later");
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    // Reset page to 1 on category change
    useEffect(() => {
        setPage(1);
    }, [selectedCat]);

    // Fetch on change
    useEffect(() => {
        fetchSubService();
    }, [selectedCat, page, limit]);

    const handleEditClick = (subService) => {
        navigate('/create-edit-subcategory', {
            state: {
                subService,
                categories,
                isEdit: true,
            }
        });
    };

    const handleAddClick = () => {
        navigate('/create-edit-subcategory', {
            state: {
                categories,
                isEdit: false,
            }
        });
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleLimitChange = (event) => {
        setLimit(Number(event.target.value));
        setPage(1); // reset page
    };

    return (
        <Box sx={{ backgroundColor: "#F8F9FB", p: { xs: 2, sm: 3 } }}>
            {/* Header Section */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3,
                }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Sub Categories {selectedCat ? `for ${categories.find(c => c.id === selectedCat)?.name || ''}` : ''}
                </Typography>

                {/* Filters & Add Button */}
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>


                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        sx={{
                            borderRadius: "25px",
                            px: 2,
                            py: 1,
                            backgroundColor: "#E94B1D",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            textTransform: "none",
                            '&:hover': {
                                backgroundColor: "#d13c0f",
                            },
                        }}
                    >
                        <Plus size={20} />
                        Add Sub Category
                    </Button>
                </Box>
            </Box>

            {/* Pagination controls */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select Category</InputLabel>
                    <Select
                        label="Select Category"
                        value={selectedCat}
                        onChange={(e) => setSelectedCat(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>All Categories</em>
                        </MenuItem>
                        {categories?.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Rows per page</InputLabel>
                    <Select value={limit} label="Rows per page" onChange={handleLimitChange}>
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                    </Select>
                </FormControl>

                {/* <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    Total Sub Categories: {totalItems}
                </Typography> */}
            </Box>

            {/* Table / Loading / Empty */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100px" mt={2}>
                    <CircularProgress />
                </Box>
            ) : subServices.length ? (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead >
                            <TableRow>
                                <TableCell><strong>Image</strong></TableCell>
                                <TableCell><strong>Sub Category</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subServices.map((subService) => (
                                <TableRow key={subService.id} hover>
                                    <TableCell>
                                        <img
                                            src={subService.imageUrl}
                                            alt="sub-category"
                                            width={45}
                                            height={45}
                                            style={{ borderRadius: 8, objectFit: "cover" }}
                                        />
                                    </TableCell>
                                    <TableCell>{subService.name}</TableCell>
                                    <TableCell>
                                        {subService.description?.length > 70
                                            ? `${subService.description.slice(0, 70)}...`
                                            : subService.description}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton color="primary" onClick={() => handleEditClick(subService)}>
                                            <Pencil size={18} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100px" mt={2}>
                    <Typography variant="h6">No subcategories available</Typography>
                </Box>
            )}

            {/* Pagination */}
            <Box mt={2} display="flex" justifyContent="center">
                <Pagination
                    count={totalPages > 0 ? totalPages : 1}
                    page={page > totalPages ? totalPages : page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    disabled={totalPages === 0}
                    siblingCount={0}
                    boundaryCount={1}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            height: 30,
                            minWidth: 30,
                            fontSize: '1rem',
                            marginTop: '10px'
                        }
                    }}
                />
            </Box>

            {/* Error message */}
            {error && (
                <Typography color="error" textAlign="center" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default DisplaySubCategory;
